import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useToast } from '@/hooks/use-toast';

type Props = {
  value: string | null;
  onChange: (url: string | null) => void;
  /**
   * Translation key prefix for all labels/errors. Must provide the same
   * sub-keys as `admin.campaigns.edit.watermark` (uploadLabel, uploading,
   * uploadHint, removeButton, uploadErrorTitle, uploadErrorFormat,
   * uploadErrorGeneric).
   */
  i18nPrefix?: string;
  /** Accepted MIME types (default PNG/JPEG). */
  accept?: string[];
  /** Preview frame: square for logos/watermarks, 16:9 for banners. */
  previewAspect?: 'square' | 'video';
};

const DEFAULT_ACCEPT = ['image/png', 'image/jpeg'];

export const WatermarkUploader: React.FC<Props> = ({
  value,
  onChange,
  i18nPrefix = 'admin.campaigns.edit.watermark',
  accept = DEFAULT_ACCEPT,
  previewAspect = 'square',
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getUploadUrl } = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onSelectFile = async (file: File) => {
    if (!file) return;
    if (!accept.includes(file.type)) {
      toast({
        variant: 'destructive',
        title: t(`${i18nPrefix}.uploadErrorTitle`),
        description: t(`${i18nPrefix}.uploadErrorFormat`),
      });
      return;
    }
    setUploading(true);
    const url = await getUploadUrl(file);
    setUploading(false);
    if (url) {
      onChange(url);
    } else {
      toast({
        variant: 'destructive',
        title: t(`${i18nPrefix}.uploadErrorTitle`),
        description: t(`${i18nPrefix}.uploadErrorGeneric`),
      });
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept.join(',')}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelectFile(f);
          e.target.value = '';
        }}
      />
      {value ? (
        <div className="flex items-center gap-3 rounded-md border border-border p-2">
          <div
            data-testid="uploader-preview"
            className={
              previewAspect === 'video'
                ? 'w-32 aspect-video rounded bg-gradient-to-br from-zinc-800 to-zinc-600 overflow-hidden'
                : 'w-16 h-16 rounded bg-gradient-to-br from-zinc-800 to-zinc-600 flex items-center justify-center overflow-hidden'
            }
          >
            <img
              src={value}
              alt=""
              className={previewAspect === 'video' ? 'w-full h-full object-cover' : 'max-w-full max-h-full object-contain'}
            />
          </div>
          <div className="flex-1 min-w-0 text-sm text-muted-foreground truncate">{value}</div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(null)}
            aria-label={t(`${i18nPrefix}.removeButton`)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t(`${i18nPrefix}.uploading`)}</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" />{t(`${i18nPrefix}.uploadLabel`)}</>
          )}
        </Button>
      )}
      <p className="text-xs text-muted-foreground">{t(`${i18nPrefix}.uploadHint`)}</p>
    </div>
  );
};

export default WatermarkUploader;
